import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import type { User } from "@/Modules/Models/Domain/User";
import { MessageTypeWebSocket, type UserChangedMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import { UserStatus } from "@/Common/Models/Domain/UserStatus";

import { type UserStore, useUserStore } from "@/Modules/User/Store";
import useChatStore, { type ChatStore } from "@/Modules/Chat/Store";
import { type ProfessionalViewStore, useProfessionalViewStore } from "@/Modules/Professional/Store";

import UserApiToUserTransformer from "@/Modules/Transformers/User/UserApiToUserTransformer";

import { hasValue, isObject } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

export interface ExtraParamsUserChanged {
  userStore: UserStore;
  chatStore: ChatStore;
  professionalStore: ProfessionalViewStore;
}

export const isUserChanged = (message: unknown): message is UserChangedMessageWebSocket => {
  return isObject(message) && hasValue(message.data) && message.type === MessageTypeWebSocket.USER_CHANGED;
};

export const userChangedHandler = (
  message: UserChangedMessageWebSocket,
  {
    userStore = useUserStore(),
    professionalStore = useProfessionalViewStore(),
    chatStore = useChatStore(),
  }: ExtraParamsUserChanged
): Promise<User> => {
  const userApiToUserTransformer = new UserApiToUserTransformer();
  const user = userApiToUserTransformer.transform(message.data);

  if (typeof user.isGuest !== "boolean") {
    user.isGuest = !!userStore.user?.isGuest;
  }

  if (hasValue(userStore.user)) {
    if (userStore.user.id === user.id) {
      // CHECK VARIABLES NOT SENDED BY BACK WS SERIALIZED
      user.notificationBannerMessage = userStore.user.notificationBannerMessage;

      if (userStore.user.status !== UserStatus.ACTIVE && user.status === UserStatus.ACTIVE) {
        chatStore.reconnectedSocket();
      }

      userStore.user = { ...userStore.user, ...user };
      userStore.updateLocale(user.language);
    }

    if (userStore.getUserInTherapistShow && isValidString(userStore.user.therapistId)) {
      professionalStore.fetchProfessionalInfo(userStore.user.therapistId);
    }
  }

  return Promise.resolve(user);
};

export default {
  isType: isUserChanged,
  handler: userChangedHandler,
} as EventFilter<ExtraParamsUserChanged>;
